/**
 * 바우처 상세 페이지
 */

import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Row, Col, Badge, Button, Modal, Alert, Form } from 'react-bootstrap';
import AdminLayout from '../../components/layout/AdminLayout';
import { voucherService } from '../../services/voucherService';
import { Voucher, VoucherStatus } from '../../types/voucher';
import { format } from 'date-fns';

const VoucherDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showValidateModal, setShowValidateModal] = useState(false);
  const [voucherCode, setVoucherCode] = useState('');
  const [cancelReason, setCancelReason] = useState('');

  const { data: voucher, isLoading, error } = useQuery({
    queryKey: ['voucher', id],
    queryFn: () => voucherService.getVoucher(Number(id)),
    enabled: !!id,
  });

  const cancelMutation = useMutation({
    mutationFn: (reason: string) => voucherService.cancelVoucher(Number(id), reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['voucher', id] });
      queryClient.invalidateQueries({ queryKey: ['vouchers'] });
      setShowCancelModal(false);
      setCancelReason('');
    },
  });

  const resendEmailMutation = useMutation({
    mutationFn: () => voucherService.resendEmail(Number(id)),
    onSuccess: () => {
      alert('이메일이 재발송되었습니다.');
    },
  });

  const getStatusBadge = (status: VoucherStatus) => {
    const statusMap: Record<VoucherStatus, { variant: string; label: string }> = {
      issued: { variant: 'success', label: '발행됨' },
      exchanged: { variant: 'info', label: '교환됨' },
      expired: { variant: 'warning', label: '만료됨' },
      cancelled: { variant: 'danger', label: '취소됨' },
    };

    const statusInfo = statusMap[status] || { variant: 'secondary', label: status };
    return <Badge bg={statusInfo.variant}>{statusInfo.label}</Badge>;
  };

  const isExpired = voucher ? new Date(voucher.valid_to) < new Date() : false;
  const canCancel = voucher && voucher.status === 'issued' && !isExpired;
  const canResendEmail = voucher && voucher.status === 'issued';

  if (isLoading) {
    return (
      <AdminLayout>
        <div className="text-center py-5">
          <p style={{ color: '#fff' }}>로딩 중...</p>
        </div>
      </AdminLayout>
    );
  }

  if (error || !voucher) {
    return (
      <AdminLayout>
        <div className="text-center py-5">
          <Alert variant="danger">바우처 정보를 불러올 수 없습니다.</Alert>
          <Button variant="secondary" onClick={() => navigate('/admin/vouchers')}>
            목록으로
          </Button>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div>
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h1 style={{ color: '#fff' }}>바우처 상세</h1>
          <div>
            <Button variant="secondary" onClick={() => navigate('/admin/vouchers')} className="me-2">
              목록으로
            </Button>
            {canResendEmail && (
              <Button
                variant="info"
                onClick={() => resendEmailMutation.mutate()}
                disabled={resendEmailMutation.isPending}
                className="me-2"
              >
                {resendEmailMutation.isPending ? '발송 중...' : '이메일 재발송'}
              </Button>
            )}
            {canCancel && (
              <Button variant="danger" onClick={() => setShowCancelModal(true)}>
                바우처 취소
              </Button>
            )}
          </div>
        </div>

        <Row>
          <Col md={8}>
            <Card className="mb-4">
              <Card.Header>
                <h5 className="mb-0">바우처 정보</h5>
              </Card.Header>
              <Card.Body>
                <Row>
                  <Col md={6}>
                    <div className="detail-item mb-3">
                      <label className="detail-label">바우처번호</label>
                      <div className="detail-value">{voucher.voucher_number}</div>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="detail-item mb-3">
                      <label className="detail-label">상태</label>
                      <div className="detail-value">{getStatusBadge(voucher.status)}</div>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="detail-item mb-3">
                      <label className="detail-label">바우처 코드</label>
                      <div className="detail-value">
                        <code style={{ fontSize: '14px', backgroundColor: 'rgba(255, 255, 255, 0.1)', padding: '4px 8px', borderRadius: '4px' }}>
                          {voucher.voucher_code}
                        </code>
                      </div>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="detail-item mb-3">
                      <label className="detail-label">유효기간</label>
                      <div className="detail-value">
                        {format(new Date(voucher.valid_from), 'yyyy년 MM월 dd일')} ~{' '}
                        <span className={isExpired ? 'text-danger' : ''}>
                          {format(new Date(voucher.valid_to), 'yyyy년 MM월 dd일')}
                        </span>
                        {isExpired && <Badge bg="danger" className="ms-2">만료됨</Badge>}
                      </div>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="detail-item mb-3">
                      <label className="detail-label">발행일시</label>
                      <div className="detail-value">
                        {format(new Date(voucher.created_at), 'yyyy년 MM월 dd일 HH:mm:ss')}
                      </div>
                    </div>
                  </Col>
                  {voucher.exchanged_at && (
                    <Col md={6}>
                      <div className="detail-item mb-3">
                        <label className="detail-label">교환일시</label>
                        <div className="detail-value">
                          {format(new Date(voucher.exchanged_at), 'yyyy년 MM월 dd일 HH:mm:ss')}
                        </div>
                      </div>
                    </Col>
                  )}
                  {voucher.cancelled_at && (
                    <Col md={6}>
                      <div className="detail-item mb-3">
                        <label className="detail-label">취소일시</label>
                        <div className="detail-value">
                          {format(new Date(voucher.cancelled_at), 'yyyy년 MM월 dd일 HH:mm:ss')}
                        </div>
                      </div>
                    </Col>
                  )}
                </Row>
              </Card.Body>
            </Card>

            <Card className="mb-4">
              <Card.Header>
                <h5 className="mb-0">예약 정보</h5>
              </Card.Header>
              <Card.Body>
                <Row>
                  <Col md={6}>
                    <div className="detail-item mb-3">
                      <label className="detail-label">예약번호</label>
                      <div className="detail-value">
                        {voucher.booking?.booking_number || '-'}
                      </div>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="detail-item mb-3">
                      <label className="detail-label">방문일</label>
                      <div className="detail-value">
                        {voucher.booking?.visit_date 
                          ? format(new Date(voucher.booking.visit_date), 'yyyy년 MM월 dd일')
                          : '-'}
                        {voucher.booking?.visit_time && ` ${voucher.booking.visit_time}`}
                      </div>
                    </div>
                  </Col>
                </Row>
                {voucher.booking && (
                  <Button
                    variant="link"
                    onClick={() => navigate(`/admin/bookings/${voucher.booking_id}`)}
                    style={{ padding: 0, color: 'var(--color-accent-cyan)' }}
                  >
                    예약 상세 보기 →
                  </Button>
                )}
              </Card.Body>
            </Card>

            {voucher.tickets && voucher.tickets.length > 0 && (
              <Card>
                <Card.Header>
                  <h5 className="mb-0">교환된 티켓</h5>
                </Card.Header>
                <Card.Body>
                  <div className="d-flex flex-wrap gap-2">
                    {voucher.tickets.map((ticket) => (
                      <Badge key={ticket.id} bg="info" className="p-2">
                        {ticket.ticket_number} ({ticket.status})
                      </Badge>
                    ))}
                  </div>
                </Card.Body>
              </Card>
            )}
          </Col>

          <Col md={4}>
            {voucher.qr_code && (
              <Card>
                <Card.Header>
                  <h5 className="mb-0">QR 코드</h5>
                </Card.Header>
                <Card.Body className="text-center">
                  <img src={voucher.qr_code} alt="QR Code" style={{ maxWidth: '100%' }} />
                  <p className="mt-3 mb-0">
                    <small className="text-muted">{voucher.voucher_code}</small>
                  </p>
                </Card.Body>
              </Card>
            )}
          </Col>
        </Row>

        {/* 취소 확인 모달 */}
        <Modal show={showCancelModal} onHide={() => setShowCancelModal(false)}>
          <Modal.Header closeButton>
            <Modal.Title>바우처 취소 확인</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Alert variant="warning">
              정말로 이 바우처를 취소하시겠습니까?
              <div className="mt-2">
                <strong>바우처번호:</strong> {voucher.voucher_number}<br />
                <strong>예약번호:</strong> {voucher.booking?.booking_number || '-'}
              </div>
            </Alert>
            <Form.Group className="mt-3">
              <Form.Label>취소 사유</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                placeholder="취소 사유를 입력하세요"
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowCancelModal(false)}>
              닫기
            </Button>
            <Button
              variant="danger"
              onClick={() => cancelMutation.mutate(cancelReason)}
              disabled={cancelMutation.isPending || !cancelReason.trim()}
            >
              {cancelMutation.isPending ? '처리 중...' : '취소하기'}
            </Button>
          </Modal.Footer>
        </Modal>
      </div>

      <style>{`
        .detail-item {
          padding: 8px 0;
        }
        .detail-label {
          display: block;
          font-size: 12px;
          color: var(--color-text-secondary);
          margin-bottom: 4px;
          font-weight: 500;
        }
        .detail-value {
          font-size: 14px;
          color: var(--color-text-primary);
        }
      `}</style>
    </AdminLayout>
  );
};

export default VoucherDetail;

